import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { LocalizedName } from '@/components/localized-name';
import {
  BenefitsLabel,
  OriginCell,
  PaidCell,
  PeriodBadge,
} from '@/features/manage-subscriptions/subscription-cells';
import {
  useSubscriptionSearch,
  type SubscriptionSearchModel,
} from '@/features/manage-subscriptions/subscriptions.api';
import { formatDate, formatNumber, formatPhone, initials } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * The subscriptions behind one tile on the overview.
 *
 * A drawer rather than a jump to the management grid: opening a tile is a
 * question about that number, and answering it should not cost the reader the
 * page they were reading. The grid is still one click away at the bottom, for
 * when the answer turns into work.
 *
 * The rows come from the same `MembershipSearchModel` the tile was computed
 * from, with only the tile's own filter patched over it, so the list can never
 * disagree with the figure that opened it.
 *
 * The way through is the caller's to decide: a tile whose filter the grid has
 * no control for cannot be reproduced there, and a button that lands on a
 * different set than the one being read is worse than no button, so it is
 * simply not rendered.
 */

const PAGE_SIZE = 20;

export function SubscriptionRowsSheet({
  model,
  patch,
  title,
  link,
  open,
  onOpenChange,
}: {
  model: SubscriptionSearchModel;
  patch: Partial<SubscriptionSearchModel> | null;
  title: string;
  /** Omitted where this view cannot be reproduced on the grid. */
  link?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);

  // A new question starts at page one rather than wherever the last list was.
  useEffect(() => {
    setPage(0);
  }, [patch]);

  const query = useSubscriptionSearch(
    { ...model, ...patch, page, size: PAGE_SIZE },
    open && patch !== null
  );

  const rows = query.data?.memberships ?? [];
  const total = query.data?.total ?? 0;
  // Paging keeps the last page on screen while the next one loads.
  const busy = query.isFetching && !query.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-2xl"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>

        <div className="space-y-3 p-4">
          <p className="text-muted-foreground text-sm">
            {query.isSuccess
              ? t('subscriptionSales.showingOf', { shown: rows.length, total })
              : ' '}
          </p>

          {/* `isLoading`: the query is off while the drawer is closed. */}
          {query.isLoading && (
            <div className="divide-y rounded-xl border">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="space-y-2 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1.5">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-3 w-52" />
                    </div>
                    <Skeleton className="h-5 w-16" />
                  </div>
                  <Skeleton className="h-3 w-48" />
                </div>
              ))}
            </div>
          )}

          {query.isError && (
            <p className="text-muted-foreground py-16 text-center text-sm">
              {t('subscriptionSales.loadFailed')}
            </p>
          )}

          {query.isSuccess && rows.length === 0 && (
            <div className="relative rounded-xl">
              <BusyOverlay show={busy} />
              <p className="text-muted-foreground rounded-xl border border-dashed py-12 text-center text-sm">
                {t('subscriptionSales.noRows')}
              </p>
            </div>
          )}

          {rows.length > 0 && (
            <>
              <div className="relative rounded-xl">
                <BusyOverlay show={busy} />
                <div
                  className={cn(
                    'divide-y rounded-xl border',
                    busy && 'opacity-60 transition-opacity'
                  )}
                >
                  {rows.map((row) => (
                    <div key={row.id} className="space-y-2 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-2.5">
                          <Avatar className="size-8 shrink-0">
                            {row.user?.avatar && (
                              <AvatarImage src={row.user.avatar} alt="" />
                            )}
                            <AvatarFallback>
                              {initials(row.user?.firstName, row.user?.lastName)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="truncate font-medium">
                              {row.user?.completeName ?? '—'}
                            </p>
                            <p className="text-muted-foreground truncate text-xs">
                              {row.user?.email}
                            </p>
                            {row.user?.phoneNumber && (
                              <p className="text-muted-foreground truncate text-xs tabular-nums">
                                {formatPhone(row.user.phoneNumber)}
                              </p>
                            )}
                          </div>
                        </div>
                        <PaidCell row={row} />
                      </div>

                      <div>
                        <LocalizedName
                          nameEn={row.subscriptionNameEn}
                          nameEs={row.subscriptionNameEs}
                          internalName={row.subscriptionInternalName}
                        />
                        <p className="text-muted-foreground text-xs">
                          {formatDate(row.startDate)} – {formatDate(row.endDate)}
                        </p>
                      </div>

                      <div className="flex flex-wrap items-center gap-1.5">
                        <PeriodBadge row={row} />
                        <OriginCell row={row} />
                      </div>

                      <p className="text-muted-foreground text-xs">
                        {t('subscriptionSales.benefits')}:{' '}
                        <BenefitsLabel row={row} /> ·{' '}
                        {t('subscriptionSales.bookings')}:{' '}
                        {formatNumber(row.bookingsCount)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <Pagination
                page={page}
                size={PAGE_SIZE}
                total={total}
                onPageChange={setPage}
              />
            </>
          )}

          {/* The drawer answers the question; the grid is where it gets acted
              on, so the way through stays one click away — when there is one. */}
          {link && (
            <Button variant="outline" className="w-full" asChild>
              <Link to={link}>
                <ExternalLink />
                {t('subscriptionSales.openInSubscriptions')}
              </Link>
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
